import type { FC } from "react";
import React, { useEffect } from "react";
import { RecordingMenuPopover } from "./RecordingMenuPopover";
import { RecordingToolPreviewDialog } from "./RecordingToolPreviewDialog";
import { PermissionDeniedModal } from "./PermissionDeniedModal";
import { RecordingEvents, RiveEvents } from "./animations/RecordAnimationUtils";
import type { IRecordingToolSession, MediaStreamType, TypedMediaStream } from "../../../../logic/recordingTool/recordingToolSession";
import { RecordingState, RecordingToolSingleSession } from "../../../../logic/recordingTool/recordingToolSession";
import { useRecordingSession } from "../../hooks/RecordingTool/UseRecordingSession";
import { useRecordingToolPermissionDenied } from "../../hooks/RecordingTool/UseRecordingToolPermissionDenied";
import {
    addNoCameraAvailableScreen,
    addRecordLabel,
    openPendingPipWindow,
    setupCameraPipWindow
} from "./pipWindow/pipWindow";
import type { RecorderLoggingData } from "../../../../logic/common/logging/recorderClientLogging";
import { RecorderLoggingEventType, reportRecorderError } from "../../../../logic/common/logging/recorderClientLogging";
import { PendoService } from "../../../pendoService";
import { useRecordingState } from "../../hooks/RecordingTool/UseRecordingState";
import { getCameraWindowSize, permissionsDeniedHandler } from "../../../../logic/recordingTool/recordingToolUtils";
import { NotificationInfoText, setNeutralNotification } from "../../../editor/Nooks";
import { localRecordingToolSession, localRecordingToolState } from "../../../model";
import type { RecorderError } from "../../../../logic/recordingTool/recorderError";

let pipWindow: Window;
let removePipUnloadListener: (() => void) | null = null;
let messageChannel: MessageChannel;
let recordedFile: Blob;

const messageHandlers: Record<string, (session: RecordingToolSingleSession) => void> = {
    [RecordingEvents.StartRecording]: (session) => {
        PendoService.getInstance().trackEvent("recorder start recording ", { mediaStreamType: session.type });
        messageChannel?.port2.postMessage(RecordingEvents.StartRecording);
        void session.startRecording();
    },
    [RecordingEvents.Pause]: (session) => {
        PendoService.getInstance().trackEvent("recorder paused recording ", { mediaStreamType: session.type });
        session.pauseRecording();
    },
    [RecordingEvents.Play]: (session) => {
        PendoService.getInstance().trackEvent("recorder resumed recording ", { mediaStreamType: session.type });
        session.resumeRecording();
    },
    [RecordingEvents.Stop]: (session) => {
        PendoService.getInstance().trackEvent("recorder finish recording ", { mediaStreamType: session.type });
        session.stopRecording();
    },
    [RecordingEvents.Retake]: (session) => {
        PendoService.getInstance().trackEvent("recorder retake recording ", { mediaStreamType: session.type });
        void session.restartRecording();
    }
};

const cleanup = () => {
    removePipUnloadListener?.();
    removePipUnloadListener = null;

    pipWindow?.close();
    pipWindow = null;

    messageChannel?.port1.close();
    messageChannel?.port2.close();
    messageChannel = null;
};

export const RecordingTool: FC = () => {
    const [recordingToolPreviewDialogOpen, setRecordingToolPreviewDialogOpen] = React.useState(false);
    const [recordingType, setRecordingType] = React.useState<MediaStreamType | null>(null);
    const { recordingToolPermissionDenied, setRecordingToolPermissionDenied } = useRecordingToolPermissionDenied();
    const { setRecordingSession, clearRecordingSession } = useRecordingSession();
    const recordingState = useRecordingState();

    const shouldBlockLeavingEditor =
        recordingState === RecordingState.Started ||
        recordingState === RecordingState.Recording ||
        recordingState === RecordingState.Paused ||
        recordingToolPreviewDialogOpen;

    useEffect(() => {
        // Listener for browser refresh/close
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (shouldBlockLeavingEditor) {
                event.preventDefault();
                event.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [shouldBlockLeavingEditor]);

    const recorderErrorHandler = (e: (Error | ErrorEvent), recorderData: RecorderLoggingData) => {
        setNeutralNotification({ message: NotificationInfoText.RecordingStopped, showCloseButton: true });
        reportRecorderError(e, recorderData);
        endSession();
    };

    const stopRecordingHandler = (blob: Blob, session: IRecordingToolSession) => {
        recordedFile = blob;
        cleanup();
        session.destroy();
        setRecordingSession(null);
        setRecordingToolPreviewDialogOpen(true);
    };

    const endSession = () => {
        // This is a callback, so we need to use the reactive var explicitly to get the current state
        if (localRecordingToolState() === RecordingState.Recording || localRecordingToolState() === RecordingState.Paused) {
            localRecordingToolSession().stopRecording();
        }
        else {
            cleanup();
            clearRecordingSession();
        }
    };

    const startRecordingToolSession = async (type: MediaStreamType, cameraDevice?: MediaDeviceInfo, microphoneDevice?: MediaDeviceInfo) => {
        try {
            const session = new RecordingToolSingleSession(
                type,
                (blob) => stopRecordingHandler(blob, session),
                (e: (Error | ErrorEvent), recorderData: RecorderLoggingData) => recorderErrorHandler(e, recorderData),
                cameraDevice,
                microphoneDevice,
            );
            setRecordingSession(session);

            setRecordingType(type);

            messageChannel = new MessageChannel();
            messageChannel.port2.onmessage = event => {
                messageHandlers[event.data]?.(session);
            };

            const streamPromise: Promise<TypedMediaStream[]> = session.startCapture();

            let dimensions: { width: number, height: number };
            if (type === "camera") {
                dimensions = getCameraWindowSize();
            }
            else {
                dimensions = { width: 304, height: 148 };
            }

            const pipWindowPromise = openPendingPipWindow(type, messageChannel?.port1, dimensions, endSession);
            const { pipWindow: returnedPip, removeUnloadListener } = await pipWindowPromise;
            pipWindow = returnedPip;
            removePipUnloadListener = removeUnloadListener;

            const stream: TypedMediaStream[] = await streamPromise;

            // wait for 0.1 second before sending the loading finished event
            await new Promise(resolve => setTimeout(resolve, 100));

            messageChannel.port2.postMessage(RiveEvents.LoadingFinished);
            addRecordLabel(pipWindow);

            if (type === "camera") {
                permissionsDeniedHandler((type) => {
                    reportRecorderError(new Error(`${type} permissions denied`), {
                        recorderEventType: type === "Camera" ? RecorderLoggingEventType.CameraPermissionsDenied : RecorderLoggingEventType.MicPermissionsDenied,
                        metadata: { permissionQueryResult: "denied", type }
                    });
                });

                PendoService.getInstance().trackEvent("recorder settings initialized ", {
                    selectedCamera: stream[0].getTracks().find(track => track.kind === "video")?.label,
                    selectedMic: stream[0].getTracks().find(track => track.kind === "audio")?.label
                });

                await setupCameraPipWindow(pipWindow, stream[0], dimensions.width, dimensions.height, handleChangeDevice, onSettingsButtonClick);
            }
        }
        catch (error) {
            if ((error as RecorderError)?.recorderEventType === RecorderLoggingEventType.NoDeviceAvailable) {
                addNoCameraAvailableScreen(pipWindow, () => {
                    cleanup();
                    clearRecordingSession();
                });
            }
            else {
                cleanup();
                clearRecordingSession();
                setRecordingType(null);
            }
        }
    };

    const onSettingsButtonClick = () => {
        PendoService.getInstance().trackEvent("recorder settings clicked ", {});
    };

    const handleChangeDevice = async (cameraDevice?: MediaDeviceInfo, microphoneDevice?: MediaDeviceInfo) => {
        const currentSession = localRecordingToolSession();

        // Don't start a new session if the devices are the same
        if (currentSession &&
            currentSession.currentMicDevice?.deviceId === microphoneDevice?.deviceId &&
            currentSession.currentCameraDevice?.deviceId === cameraDevice?.deviceId
        ) {
            return;
        }

        cleanup();
        clearRecordingSession();
        await startRecordingToolSession("camera", cameraDevice, microphoneDevice);
    };

    const handlePreviewDialogClose = () => {
        setRecordingToolPreviewDialogOpen(false);
        cleanup();
        clearRecordingSession();
    };

    return (
        <>
            <RecordingMenuPopover
                startRecordingToolSession={startRecordingToolSession}
            />
            {recordingToolPreviewDialogOpen && (
                <RecordingToolPreviewDialog
                    recordedFile={recordedFile}
                    startRecordingToolSession={startRecordingToolSession}
                    onClose={handlePreviewDialogClose}
                    recordingType={recordingType}
                />
            )}
            {recordingToolPermissionDenied && (
                <PermissionDeniedModal
                    permissionDenied={recordingToolPermissionDenied}
                    onClose={() => setRecordingToolPermissionDenied(null)}
                />
            )}
        </>
    );
};
